import { api } from './client.ts';

export interface DeleteImpact {
  activeSubscriptions: Array<{
    subscriptionId: string;
    planCode: string | null;
    displayName: string | null;
    currentPeriodEnd: string | null;
  }>;
}

export const accountApi = {
  /** Surfaces what will be lost if the user deletes their account — used to populate the confirm panel. */
  async deleteImpact(): Promise<DeleteImpact> {
    return api<DeleteImpact>('/api/account/delete-impact');
  },

  /**
   * Triggers a JSON download of the user's account data (Article 15 GDPR).
   * Uses fetch directly so we can read the blob — the api() helper assumes JSON parsing.
   */
  async downloadExport(): Promise<void> {
    const response = await fetch('/api/account/export', { credentials: 'include' });
    if (!response.ok) {
      throw new Error(`Export failed: ${response.status}`);
    }
    const blob = await response.blob();
    const filename =
      parseFilename(response.headers.get('content-disposition')) ?? 'eaa-proof-account.json';

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  /** Hard-deletes the account. Backend signs the user out; SPA must clear local state. */
  async deleteAccount(): Promise<void> {
    await api<void>('/api/account/delete', { method: 'POST' });
  },

  /**
   * Change password while signed in. Re-issues this caller's cookie with a fresh
   * SecurityStamp so they stay logged in; every other live session is signed out within
   * ~30s by the cookie-validation middleware.
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api<void>('/api/account/change-password', {
      method: 'POST',
      body: { currentPassword, newPassword },
    });
  },

  /**
   * Best-effort: ships a captured Google Ads click id (?gclid=) to the API so server-side
   * conversion uploads can attribute later purchases + renewals to the originating ad click.
   * Fire-and-forget; the API persists first-touch and silently ignores duplicates.
   */
  async recordGclid(gclid: string): Promise<void> {
    try {
      await api<void>('/api/account/gclid', { method: 'POST', body: { gclid } });
    } catch {
      /* swallow — analytics never breaks the app */
    }
  },
};

function parseFilename(header: string | null): string | null {
  if (!header) return null;
  // Content-Disposition: attachment; filename=eaa-proof-account-xxx.json
  const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(header);
  return match ? decodeURIComponent(match[1]) : null;
}
